import React, { ReactNode, useState } from 'react';
import { Button, Modal, Stack, Form, Row, Col, Image } from 'react-bootstrap';
import useTranslate from 'hooks/useTranslate';
import { useFormik } from 'formik';
import { addAdvertisementInitialValues, addAdvertisementSchema } from 'constants/schemaValidations';
import { AddAdvertisementInterface } from 'constants/interfaces';
import useCommonStore, { setIsLoading } from 'store/common';
import {
	useAddAdvertisementMutation,
	useBucketUrlMutation,
	useGetAdvertisementPriceQuery,
	useUploadFileMutation,
} from 'services';
import { imageBucketUrl } from 'utils';
import toast from 'react-hot-toast';

const AddAdvertisement: React.FC<any> = () => {
	const { translate } = useTranslate();

	const { mutateAsync: addAdvertisementMutation, isPending } = useAddAdvertisementMutation();
	const { mutateAsync: bucketUrlMutation } = useBucketUrlMutation();
	const { mutateAsync: uploadFileMutation } = useUploadFileMutation();

	const [selectedImage, setSelectedImage] = useState<string | null>(null);
	const { modalConfig = {}, hideCommonModal }: any = useCommonStore((state) => state);

	const { data: priceList } = useGetAdvertisementPriceQuery();

	const formik: any = useFormik({
		initialValues: addAdvertisementInitialValues,
		validationSchema: addAdvertisementSchema(translate),
		onSubmit: async (values: any) => {
			if (values?.image) {
				setIsLoading(true);
				const file = values?.image;
				const imgPath = await imageBucketUrl(
					file,
					translate,
					file?.type,
					file.size,
					bucketUrlMutation,
					uploadFileMutation,
					'ADVERTISEMENT_IMAGE',
				);
				if (imgPath) {
					await addAdvertisementMutation({
						...values,
						image: imgPath,
					})
						.then((res: any) => {
							if (res?.success) {
								formik.resetForm();
								toast.success(res?.message);
							}
						})
						.finally(() => (modalConfig?.onClose ? modalConfig?.onClose() : hideCommonModal()));
				}
				setIsLoading(false);
			}
		},
	});

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onload = () => {
				setSelectedImage(reader.result as string);
			};
			reader.readAsDataURL(file);

			// Set file in Formik
			formik.setFieldValue('image', file);
		}
	};

	const renderError = <T extends keyof AddAdvertisementInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const handleDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (/^\d*$/.test(value)) {
			formik.setFieldValue('days', value);
		} else if (value === '') {
			formik.setFieldValue('days', '');
		}
	};

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => (modalConfig?.onClose ? modalConfig?.onClose() : hideCommonModal())}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Advertisement.addAdvertisement')}</h4>
					<Row>
						{selectedImage && (
							<Col md="12" className="text-center mb-4">
								<Image
									thumbnail
									style={{ width: '150px', height: '150px' }}
									src={selectedImage}
									alt="Uploaded Image"
									className="object-fit-cover"
									loading="lazy"
								/>
							</Col>
						)}

						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Advertisement.banner')}</Form.Label>
								<Form.Control
									type="file"
									placeholder={translate('Extras.uploadBanner')}
									className="rounded-pill"
									name="image"
									accept="image/*"
									onChange={handleImageChange} // Set file input handler
									onBlur={formik.handleBlur}
								/>
								{renderError('image')}
							</Form.Group>
						</Col>

						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.title')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.title')}
									className="rounded-pill"
									name="title"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.companyName}
									autoComplete="off"
								/>
								{renderError('title')}
							</Form.Group>
						</Col>

						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.link')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.link')}
									className="rounded-pill"
									name="link"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.link}
									autoComplete="off"
								/>
								{renderError('link')}
							</Form.Group>
						</Col>

						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Advertisement.days')}</Form.Label>
								<Form.Control
									type="number"
									min="1"
									max="365"
									placeholder={translate('Advertisement.days')}
									className="rounded-pill"
									name="days"
									onChange={handleDaysChange}
									onBlur={formik.handleBlur}
									value={formik.values.days}
									autoComplete="off"
								/>
								{renderError('days')}
							</Form.Group>
						</Col>

						<Col md="12">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.description')}</Form.Label>
								<Form.Control
									as="textarea"
									rows={4}
									placeholder={translate('PlaceHolder.writeHere')}
									name="description"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.description}
									autoComplete="off"
								/>
								{renderError('description')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex align-items-center gap-4">
						<Button className="filled-btn rounded-pill" type="submit" disabled={isPending}>
							{translate('Advertisement.publish')}
						</Button>
						<div className="">
							{formik?.values?.days > 0 && (
								<span className="price-list">
									{priceList?.currency} {formik?.values?.days * priceList?.price}
								</span>
							)}
						</div>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default AddAdvertisement;
